import { ErrorOutline } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { useStudioUnauthenticatedContext } from '../../context/StudioContext';
import { useFeatureFlags } from '../../util/useFeatureFlags';
import { AuthCard } from './AuthCard';
import './LoginForm.css';

interface LoginFormProps {
  onSignUpClick?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSignUpClick }) => {
  const theme = useTheme();
  const { login } = useStudioUnauthenticatedContext();
  const { isEnabled } = useFeatureFlags();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionTimeoutSec, setSessionTimeoutSec] = useState<number>(
    24 * 60 * 60,
  ); // 24h
  const [refreshTimeoutSec, setRefreshTimeoutSec] = useState<number>(
    30 * 24 * 60 * 60,
  ); // 30d

  const isLightTheme = theme.palette.mode === 'light';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const debugSettings = isEnabled('debugTimeouts')
        ? {
            sessionDurationSec: sessionTimeoutSec,
            refreshDurationSec: refreshTimeoutSec,
          }
        : undefined;

      await login(email, password, debugSettings);
    } catch (err) {
      setError("We couldn't find an account matching these credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box className="login-form-container">
      <AuthCard title="Welcome back!">
        <form onSubmit={handleSubmit}>
          <Box className="login-form">
            {/* Email Field */}
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null); // Clear error when user starts typing
              }}
              variant="outlined"
              className={`login-form-field ${isLightTheme ? 'light' : ''}`}
            />

            {/* Password Field */}
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null); // Clear error when user starts typing
              }}
              variant="outlined"
              className={`login-form-field ${isLightTheme ? 'light' : ''}`}
            />

            {/* Debug Timeout Settings */}
            {isEnabled('debugTimeouts') && (
              <Box
                sx={{ mt: 2, p: 2, border: '1px dashed #ccc', borderRadius: 1 }}
              >
                <Typography variant="h6" sx={{ mb: 2, color: 'warning.main' }}>
                  🐛 Debug Timeouts
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Session Timeout</InputLabel>
                  <Select
                    value={sessionTimeoutSec}
                    onChange={(e) =>
                      setSessionTimeoutSec(Number(e.target.value))
                    }
                    label="Session Timeout"
                  >
                    <MenuItem value={5}>5 seconds</MenuItem>
                    <MenuItem value={30}>30 seconds</MenuItem>
                    <MenuItem value={60}>1 minute</MenuItem>
                    <MenuItem value={60 * 5}>5 minutes</MenuItem>
                    <MenuItem value={60 * 60}>1 hour</MenuItem>
                    <MenuItem value={24 * 60 * 60}>24 hours (default)</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Refresh Timeout</InputLabel>
                  <Select
                    value={refreshTimeoutSec}
                    onChange={(e) =>
                      setRefreshTimeoutSec(Number(e.target.value))
                    }
                    label="Refresh Timeout"
                  >
                    <MenuItem value={10}>10 seconds</MenuItem>
                    <MenuItem value={60}>1 minute</MenuItem>
                    <MenuItem value={60 * 5}>5 minutes</MenuItem>
                    <MenuItem value={60 * 60}>1 hour</MenuItem>
                    <MenuItem value={24 * 60 * 60}>1 day</MenuItem>
                    <MenuItem value={7 * 24 * 60 * 60}>7 days</MenuItem>
                    <MenuItem value={30 * 24 * 60 * 60}>
                      30 days (default)
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Links Row */}
            <Box className="links-row">
              <Typography
                variant="body1"
                className={`forgot-password-text ${isLightTheme ? 'light' : ''}`}
              >
                Forgot password?
              </Typography>
              <Link
                href="mailto:support@feed.fm"
                underline="always"
                className="contact-support-link"
              >
                Contact Support
              </Link>
            </Box>

            {/* Login Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              className="login-button"
            >
              {isLoading ? 'Logging in...' : 'Log in'}
            </Button>
          </Box>
        </form>
      </AuthCard>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          icon={<ErrorOutline />}
          className={`error-alert ${isLightTheme ? 'light' : ''}`}
        >
          <Typography variant="h6" className="error-title">
            No account found
          </Typography>
          <Typography variant="body2" className="error-message">
            {error}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};
